import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Eye, Calendar, Users } from "lucide-react";
import ProjectChatFiltersConfigDialog from "@/components/ProjectChatFiltersConfigDialog";
import { useProjects } from "@/hooks/useProjects";
import { isProjectMember } from "@/hooks/useProjectAccess";
import { useCurrentUser } from "@/hooks/useUsers";
import { useAuth } from "@/contexts/AuthContext";
import PageLayout from "@/components/PageLayout";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { projectTypeLabel, projectTypeVariant } from "@/config/projectTypeConfig";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const PAGE_SIZE = 10;


const AllProjects = () => {
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const { user } = useAuth();
  const isLoading = projectsLoading || userLoading;

  const [page, setPage] = useState(1);

  const isAdmin = user?.role === "Admin";

  // Admins see every project so they can manage setup and add themselves to a
  // team; opening a project's contents still requires membership.
  const filtered = [...projects]
    .filter((p) => isAdmin || !currentUser || isProjectMember(p, currentUser.id))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("ellipsis");
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <PageLayout>
      <PageHeader title="Projects" subtitle="View and manage all projects" />

      <div className="flex justify-end mb-4">
        <Button asChild size="sm" className="gap-1 bg-brand-teal text-white hover:bg-brand-teal/90">
          <Link to="/projects/new">
            <Plus className="w-4 h-4" /> Start New Project
          </Link>
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-40 whitespace-nowrap">Status</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  Loading projects…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && paginated.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  No projects yet.
                </TableCell>
              </TableRow>
            )}
            {paginated.map((project) => (
              <TableRow key={project.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-foreground">{project.name}</span>
                    {project.projectType && (
                      <Badge variant={projectTypeVariant[project.projectType] ?? "secondary"}>
                        {projectTypeLabel[project.projectType] ?? project.projectType}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                    <span className="font-medium">{project.code}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Started {project.startDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {project.teamMembers} team members
                    </span>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <StatusBadge status={project.status} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {(isAdmin || project.createdBy === currentUser?.id) && (
                      <ProjectChatFiltersConfigDialog
                        projectId={project.id}
                        projectName={project.name}
                        visibleKeys={project.visibleChatFilterKeys}
                      />
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      asChild
                      title="Edit project"
                    >
                      <Link to={`/projects/${project.id}/edit`}>
                        <Pencil className="w-4 h-4" />
                      </Link>
                    </Button>
                    {isProjectMember(project, currentUser?.id) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        asChild
                        title="View project"
                        className="text-brand-teal hover:white"
                      >
                        <Link to={`/projects/${project.id}`}>
                          <Eye className="w-4 h-4" />
                        </Link>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-disabled={currentPage === 1}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {getPageNumbers().map((p, i) =>
                p === "ellipsis" ? (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === currentPage}
                      onClick={() => setPage(p)}
                      className="cursor-pointer"
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-disabled={currentPage === totalPages}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </PageLayout>
  );
};

export default AllProjects;
